import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, ListPlus } from 'lucide-react';
import type { AttributeValueToken } from '@sleed/bizcommerce-api';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { Field } from '~/components/promotion/editor-section';
import { ImageUploader, type UploadedImage } from '@/components/image-uploader';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useAttributeValuesList, useAttributeValueMutations } from '~/hooks/use-attribute-values';
import { attributeValueSchema, emptyAttributeValue, tokenToForm, type AttributeValueFormValues } from '~/lib/schemas/attribute-value';

/** Adapt a persisted image URL to the shape ImageUploader renders (no File ⇒ an existing server image). */
function urlToImage(url: string | undefined): UploadedImage | null {
	if (!url) return null;
	return { url, name: url.split('/').pop() || url, width: 0, height: 0, size: 0 };
}

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** The parent attribute whose values are managed; null when the sheet is closed. */
	attribute: { id: number; nm: string } | null;
}

/** Manage the list-of-values for a ListOfValues attribute (parity with the legacy "Τιμές" modal). */
export function AttributeValuesSheet({ open, onOpenChange, attribute }: Props) {
	const { t } = useTranslation();
	const attributeId = attribute?.id;
	const { data, isLoading, isError, refetch } = useAttributeValuesList(open ? attributeId : undefined);
	const { remove } = useAttributeValueMutations(attributeId);

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<AttributeValueFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<AttributeValueToken | null>(null);

	const columns = useMemo<SystemGridColumn<AttributeValueToken>[]>(
		() => [
			{
				key: 'value',
				header: t('attributes.values.columns.value'),
				width: 'minmax(0,1fr)',
				sortValue: v => v.tval ?? '',
				exportValue: v => v.tval ?? '',
				render: v => <span className="truncate text-sm font-medium text-foreground">{v.tval}</span>
			},
			{
				key: 'code',
				header: t('attributes.values.columns.code'),
				width: '140px',
				sortValue: v => v.cd ?? '',
				exportValue: v => v.cd ?? '',
				render: v => <span className="font-mono text-xs text-foreground-muted">{v.cd}</span>
			},
			{
				key: 'color',
				header: t('attributes.values.columns.color'),
				width: '90px',
				align: 'center',
				exportValue: v => v.chex ?? '',
				render: v =>
					v.chex ? (
						<span className="inline-flex items-center gap-1.5">
							<span className="size-4 rounded border border-border" style={{ backgroundColor: v.chex }} />
						</span>
					) : (
						<span className="text-foreground-disabled">—</span>
					)
			},
			{
				key: 'image',
				header: t('attributes.values.image'),
				width: '80px',
				align: 'center',
				exportValue: v => v.img1 ?? '',
				render: v =>
					v.img1 ? (
						<img src={v.img1} alt="" className="size-8 rounded border border-border object-contain" />
					) : (
						<span className="text-foreground-disabled">—</span>
					)
			},
			{
				key: 'visible',
				header: t('attributes.values.columns.visible'),
				width: '80px',
				align: 'center',
				sortValue: v => (v.iv ? 1 : 0),
				exportValue: v => (v.iv ? t('common.yes') : t('common.no')),
				render: v => (v.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('attributes.values.columns.sort'),
				width: '70px',
				align: 'right',
				sortValue: v => v.si ?? 0,
				exportValue: v => v.si ?? 0,
				render: v => <span className="text-sm tabular-nums text-foreground-muted">{v.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-3xl">
				<SheetHeader className="border-b border-border px-5 py-4">
					<SheetTitle>{t('attributes.values.title')}</SheetTitle>
					<SheetDescription>{attribute?.nm}</SheetDescription>
				</SheetHeader>

				<div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
					<SystemGrid<AttributeValueToken>
						gridId="attribute-values"
						title={t('attributes.values.title')}
						rows={data ?? []}
						loading={isLoading}
						error={isError}
						onRetry={() => void refetch()}
						getRowId={v => v.id ?? `${v.tval}`}
						columns={columns}
						searchPredicate={(v, q) => `${v.tval ?? ''} ${v.cd ?? ''}`.toLowerCase().includes(q)}
						defaultSort={{ key: 'sort', dir: 'asc' }}
						emptyIcon={<ListPlus size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
						addLabel={t('attributes.values.add')}
						onAdd={() => {
							if (!attributeId) return;
							setEditing(emptyAttributeValue(attributeId));
							setEditOpen(true);
						}}
						onEdit={v => {
							setEditing(tokenToForm(v));
							setEditOpen(true);
						}}
						onDelete={setDeleteRow}
						exportFilename="attribute-values"
					/>
				</div>

				<AttributeValueEditDialog open={editOpen} onOpenChange={setEditOpen} attributeId={attributeId} initial={editing} />

				<Dialog open={Boolean(deleteRow)} onOpenChange={o => !o && setDeleteRow(null)}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>{t('attributes.values.deleteTitle')}</DialogTitle>
							<DialogDescription>{t('attributes.values.deleteBody', { name: deleteRow?.tval ?? '' })}</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button variant="ghost" onClick={() => setDeleteRow(null)}>
								{t('common.cancel')}
							</Button>
							<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
								{t('common.delete')}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</SheetContent>
		</Sheet>
	);
}

interface ValueDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	attributeId: number | undefined;
	initial: AttributeValueFormValues | null;
}

/** Add / edit a single attribute value (value text + code + color + visible + sort). */
function AttributeValueEditDialog({ open, onOpenChange, attributeId, initial }: ValueDialogProps) {
	const { t } = useTranslation();
	const { create, update } = useAttributeValueMutations(attributeId);
	const form = useForm<AttributeValueFormValues>({
		resolver: zodResolver(attributeValueSchema) as Resolver<AttributeValueFormValues>,
		defaultValues: initial ?? emptyAttributeValue(attributeId ?? 0),
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, control, formState } = form;
	const isEdit = Boolean(initial?.id);

	// Single image slot, handled out-of-band (binaryRepoImagesApi upload + imgdel1 flag).
	const [img1, setImg1] = useState<UploadedImage | null>(null);

	useEffect(() => {
		if (open) {
			reset(initial ?? emptyAttributeValue(attributeId ?? 0));
			setImg1(urlToImage(initial?.img1));
		}
	}, [open, initial, attributeId, reset]);

	const onSubmit = handleSubmit(async values => {
		const images = { slot1: img1?.file ?? null, delete1: Boolean(initial?.img1) && !img1 };
		try {
			if (values.id) await update.mutateAsync({ form: values, images });
			else await create.mutateAsync({ form: values, images });
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const tErr = (message?: string) => (message ? t(message, { defaultValue: message }) : undefined);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('attributes.values.editTitle') : t('attributes.values.addTitle')}</DialogTitle>
					<DialogDescription>{t('attributes.values.formSubtitle')}</DialogDescription>
				</DialogHeader>

				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<Field label={t('attributes.values.columns.value')} error={tErr(formState.errors.tval?.message)}>
						<Input {...register('tval')} aria-invalid={Boolean(formState.errors.tval)} autoFocus />
					</Field>
					<div className="grid grid-cols-2 gap-4">
						<Field label={t('attributes.values.columns.code')} error={tErr(formState.errors.cd?.message)}>
							<Input {...register('cd')} maxLength={50} />
						</Field>
						<Field
							label={t('attributes.values.columns.color')}
							error={tErr(formState.errors.chex?.message)}
							help={t('attributes.values.colorHelp')}>
							<Controller
								control={control}
								name="chex"
								render={({ field }) => (
									<div className="flex items-center gap-2">
										<input
											type="color"
											aria-label={t('attributes.values.columns.color')}
											value={field.value && /^#[0-9a-fA-F]{6}$/.test(field.value) ? field.value : '#000000'}
											onChange={e => field.onChange(e.target.value)}
											className="size-9 shrink-0 cursor-pointer rounded border border-border bg-surface p-0.5"
										/>
										<Input value={field.value ?? ''} onChange={e => field.onChange(e.target.value)} maxLength={10} placeholder="#RRGGBB" />
									</div>
								)}
							/>
						</Field>
					</div>
					<div className="grid grid-cols-2 gap-4">
						<Field label={t('attributes.values.columns.sort')} error={tErr(formState.errors.si?.message)}>
							<Input type="number" {...register('si', { valueAsNumber: true })} />
						</Field>
						<Field label={t('attributes.values.erpCode')} error={tErr(formState.errors.erpcd?.message)}>
							<Input {...register('erpcd')} maxLength={50} />
						</Field>
					</div>
					<Field label={t('attributes.values.image')}>
						<ImageUploader value={img1} onChange={setImg1} helpText={t('attributes.values.imageHelp')} />
					</Field>
					<div className="flex items-center justify-between rounded-md border border-border px-4 py-2">
						<Label htmlFor="attrval-visible">{t('attributes.values.columns.visible')}</Label>
						<Controller
							control={control}
							name="iv"
							render={({ field }) => <Switch id="attrval-visible" checked={Boolean(field.value)} onCheckedChange={field.onChange} />}
						/>
					</div>

					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
